import React, { useState, useEffect, useCallback } from 'react';
import ApiService from '../services/ApiService';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = {
  datetime: (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  },
  duration: (secs) => {
    if (!secs && secs !== 0) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  },
};

const STATUS_STYLES = {
  success: 'bg-green-100 text-green-800 border border-green-200',
  error:   'bg-red-100 text-red-800 border border-red-200',
  partial: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  running: 'bg-blue-100 text-blue-800 border border-blue-200',
  stopped: 'bg-gray-100 text-gray-700 border border-gray-300',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>
    {status === 'success' && '✓ '}
    {status === 'error'   && '✗ '}
    {status === 'partial' && '⚠ '}
    {status === 'running' && '⟳ '}
    {status === 'stopped' && '⏹ '}
    {status || 'unknown'}
  </span>
);

// ── Run Detail Modal ──────────────────────────────────────────────────────────
const RunDetailModal = ({ run, onClose }) => {
  const [activeTab, setActiveTab] = useState('summary');

  if (!run) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4 bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{run.id}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{fmt.datetime(run.startedAt)}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={run.status} />
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {['summary', 'log', 'errors', 'fmp'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'fmp' ? 'FMP Data' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'errors' && run.errors?.length > 0 && (
                <span className="ml-1.5 bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded-full">
                  {run.errors.length}
                </span>
              )}
              {tab === 'fmp' && run.fmpStubCount > 0 && (
                <span className="ml-1.5 bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded-full">
                  {run.fmpStubCount} stubs
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Mode', value: run.mode },
                  { label: 'Duration', value: fmt.duration(run.durationSeconds) },
                  { label: 'Records Pushed', value: run.recordsPushed ?? '—' },
                  { label: 'Push Status', value: run.pushStatus || '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{label}</div>
                    <div className="font-semibold text-gray-900 text-sm">{String(value)}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Started</div>
                  <div className="text-sm text-gray-900">{fmt.datetime(run.startedAt)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Finished</div>
                  <div className="text-sm text-gray-900">{fmt.datetime(run.finishedAt)}</div>
                </div>
              </div>

              {run.partitionsCompleted?.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-green-700 font-semibold mb-2">Partitions Completed ({run.partitionsCompleted.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {run.partitionsCompleted.map(p => (
                      <span key={p} className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">{p}</span>
                    ))}
                  </div>
                </div>
              )}

              {run.partitionsFailed?.length > 0 && (
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-xs text-red-700 font-semibold mb-2">Partitions Failed ({run.partitionsFailed.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {run.partitionsFailed.map(p => (
                      <span key={p} className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">{p}</span>
                    ))}
                  </div>
                </div>
              )}

              {run.pushMessage && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Push Message</div>
                  <div className="text-sm text-gray-900">{run.pushMessage}</div>
                </div>
              )}

              {run.logFile && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Log File</div>
                  <div className="text-xs font-mono text-gray-700 break-all">{run.logFile}</div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'log' && (
            <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto leading-relaxed">
              {run.logTail || 'No log captured for this run.'}
            </pre>
          )}

          {activeTab === 'errors' && (
            run.errors?.length > 0 ? (
              <div className="space-y-2">
                {run.errors.map((line, i) => (
                  <div key={i} className="bg-red-50 border border-red-100 rounded-lg px-4 py-2">
                    <pre className="text-xs font-mono text-red-800 whitespace-pre-wrap">{line}</pre>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-12 text-sm">No errors recorded for this run.</div>
            )
          )}

          {activeTab === 'fmp' && (
            <div>
              {run.fmpStubCount > 0 ? (
                <>
                  <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-sm text-orange-800">
                    ⚠ <strong>{run.fmpStubCount} symbol{run.fmpStubCount !== 1 ? 's' : ''}</strong> fell back to stub (zeroed) financial data due to FMP API failures. These stocks show 0 for all financial metrics.
                  </div>
                  {(run.fmpStats || []).map((part) => (
                    part.stub_count > 0 && (
                      <div key={part.partition} className="mb-4">
                        <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                          {part.partition} — {part.stub_count}/{part.total_symbols} stubs
                        </div>
                        <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="text-left px-3 py-2 font-semibold text-gray-600">Symbol</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600">Retries</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600">Reason</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(part.stub_symbols || []).map((s, i) => (
                              <tr key={i} className="hover:bg-orange-50">
                                <td className="px-3 py-2 font-mono font-semibold text-orange-700">{s.symbol}</td>
                                <td className="px-3 py-2 text-gray-600">{s.retries}</td>
                                <td className="px-3 py-2 text-gray-500 truncate max-w-xs">{s.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  ))}
                </>
              ) : (
                <div className="text-center text-green-600 py-12 text-sm">
                  ✓ All symbols received real FMP financial data — no stubs used.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const PipelineRunsPage = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchRuns = useCallback(async () => {
    try {
      const data = await ApiService.getPipelineRuns();
      setRuns(data.runs || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchRuns, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchRuns]);

  const lastSuccess = runs.find(r => r.status === 'success');
  const lastError   = runs.find(r => r.status === 'error');

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Pipeline Run History</h1>
          <p className="text-sm text-gray-500 mt-1">Records of each backend pipeline execution</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <button
            onClick={fetchRuns}
            className="px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Runs</div>
          <div className="text-3xl font-bold text-navy-900">{runs.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-4 shadow-sm">
          <div className="text-xs text-green-700 uppercase tracking-wide mb-1">Last Successful Run</div>
          <div className="text-sm font-semibold text-gray-900">{lastSuccess ? fmt.datetime(lastSuccess.finishedAt) : '—'}</div>
          {lastSuccess && <div className="text-xs text-gray-500 mt-1">{lastSuccess.recordsPushed} records · {fmt.duration(lastSuccess.durationSeconds)}</div>}
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4 shadow-sm">
          <div className="text-xs text-red-700 uppercase tracking-wide mb-1">Last Error</div>
          <div className="text-sm font-semibold text-gray-900">{lastError ? fmt.datetime(lastError.finishedAt) : 'None'}</div>
          {lastError && <div className="text-xs text-gray-500 mt-1">{lastError.errors?.length || 0} error lines captured</div>}
        </div>
        <div className={`bg-white rounded-xl p-4 shadow-sm border ${lastSuccess?.fmpStubCount > 0 ? 'border-orange-300' : 'border-gray-200'}`}>
          <div className={`text-xs uppercase tracking-wide mb-1 ${lastSuccess?.fmpStubCount > 0 ? 'text-orange-600' : 'text-gray-500'}`}>FMP Stubs (Last Run)</div>
          <div className={`text-3xl font-bold ${lastSuccess?.fmpStubCount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {lastSuccess?.fmpStubCount ?? '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {lastSuccess?.fmpStubCount > 0 ? 'symbols with zeroed financials' : lastSuccess ? '✓ all real data' : ''}
          </div>
        </div>
      </div>

      {/* FMP stub alert banner */}
      {lastSuccess?.fmpStubCount > 0 && (
        <div className="mb-4 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm text-orange-800 flex items-start gap-2">
          <span className="text-lg">⚠</span>
          <div>
            <strong>{lastSuccess.fmpStubCount} symbol{lastSuccess.fmpStubCount !== 1 ? 's' : ''}</strong> used stub (zeroed) financial data in the last run due to FMP API rate limiting.
            Click the last run row → FMP Data tab to see which symbols were affected.
          </div>
        </div>
      )}

      {/* Run table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading runs...</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 text-sm">{error}</div>
      ) : runs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-4">📋</div>
          <div className="text-lg font-medium">No pipeline runs recorded yet</div>
          <div className="text-sm mt-1">Runs will appear here after the next pipeline execution</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Run ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Started</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Duration</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Mode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Records</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Push</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Errors</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">FMP Stubs</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map((run) => (
                <tr
                  key={run.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedRun(run)}
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{run.id}</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmt.datetime(run.startedAt)}</td>
                  <td className="px-4 py-3 text-gray-600">{fmt.duration(run.durationSeconds)}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">{run.mode || '—'}</span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={run.status} /></td>
                  <td className="px-4 py-3 text-gray-700">{run.recordsPushed ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${
                      run.pushStatus === 'success' ? 'text-green-600' :
                      run.pushStatus === 'error'   ? 'text-red-600' :
                      'text-gray-400'
                    }`}>{run.pushStatus || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    {run.errors?.length > 0
                      ? <span className="text-xs font-semibold text-red-600">{run.errors.length}</span>
                      : <span className="text-xs text-gray-400">0</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    {run.fmpStubCount > 0
                      ? <span className="text-xs font-semibold text-orange-600">⚠ {run.fmpStubCount}</span>
                      : <span className="text-xs text-gray-400">0</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="text-xs text-brand-600 hover:text-brand-700 font-medium">Details →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {selectedRun && (
        <RunDetailModal run={selectedRun} onClose={() => setSelectedRun(null)} />
      )}
    </div>
  );
};

export default PipelineRunsPage;
